'use client'

import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { appConfig } from '@config/app.config.ts'
import { AuthData, AuthTokens, authTokensStorageKey, initialAuthData } from './model.ts'
import { localStorageService } from '@hooks/useWebStorage.hook.ts'
import trim from 'lodash/trim'
import type { RequestTokenFn } from '../../api/model'
import { toaster } from '@components/shared/toaster/toaster.ts'
import useEventListener from '@use-it/event-listener'
import { AppCustomEvent } from '@types'
import isEmpty from 'lodash/isEmpty'

type AuthTokenManagerState = {
  requestToken: RequestTokenFn
}

const initialContext: AuthTokenManagerState = {
  requestToken: () => initialAuthData,
}

const AuthTokenManagerContext = createContext<AuthTokenManagerState>(initialContext)

export const AuthTokenManager: FC<PropsWithChildren> = ({ children }) => {
  const requestToken = useCallback(() => {
    return readAuthData()
  }, [])

  const authTokenManager = useMemo(
    () => ({
      requestToken,
    }),
    [requestToken],
  )

  const handleUserLoggedIn = async () => {
    // user.service imports the wagmi config; load it on demand so the root provider
    // stack stays free of Web3 code until a user actually logs in.
    const [{ getUserWallets }, { WelcomeToCWToast }] = await Promise.all([
      import('@services/domain/user.service.ts'),
      import('@components/shared/toaster/custom/WelcomeToCWToast.tsx'),
    ])
    const userWallets = await getUserWallets()

    if (isEmpty(userWallets)) {
      toaster.custom(<WelcomeToCWToast />, {
        dismissOnLogout: true,
      })
    }
  }

  useEventListener(
    AppCustomEvent.USER_LOGGED_IN,
    handleUserLoggedIn,
    typeof window !== 'undefined' ? window : null,
  )

  return (
    <AuthTokenManagerContext.Provider value={authTokenManager}>
      {children}
    </AuthTokenManagerContext.Provider>
  )
}

export const useAuthTokenManager = () => useContext(AuthTokenManagerContext)

export const saveAuthData = (value: AuthData) =>
  localStorageService.setItem(authTokensStorageKey, value)

const readAuthData = () => {
  const authData = localStorageService.getItem<AuthData>(
    authTokensStorageKey,
    initialAuthData,
  )

  if (!authData || trim(String(authData)) === 'null') {
    return initialAuthData
  }

  return authData
}

export type RefreshTokenFn = (refreshToken: string) => Promise<AuthTokens | null>

export const refreshTokenFn: RefreshTokenFn = async (refreshToken: string) => {
  console.info('refreshing token')

  try {
    const response = await fetch(
      `${appConfig.apiUrl}${appConfig.endpoints.v1.auth.refreshTokens}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          refreshToken,
        }),
      },
    )

    if (!response.ok) {
      return null
    }

    return await response.json()
  } catch (error) {
    console.error('Error refreshing token', error)
    return null
  }
}
